import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from '../ErrorBoundary';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard, Building2, ClipboardList, FileText, History, FilePlus2,
  PhoneCall, Bell as BellIcon, User, ChevronDown, LogOut, Settings as SettingsIcon, Menu, X,
  Phone, Mail, Users, Boxes, Wrench,
} from 'lucide-react';
import Logo from '../Logo';
import NotificationBell from '../NotificationBell';
import AnnouncementsBanner from '../AnnouncementsBanner';
import ImpersonationBanner from '../ImpersonationBanner';
import PreviewBanner from '../PreviewBanner';
import ThemeToggle from '../ThemeToggle';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';

// Role visibility for PM portal
const ALL = ['property_manager', 'asset_manager', 'maintenance_supervisor', 'pm_maintenance'];
const PM_AM = ['property_manager', 'asset_manager']; // hide from Maintenance Supervisor + Tech (financials)
const PM_ADMINS = ['property_manager', 'asset_manager', 'maintenance_supervisor'];
const TECH_ONLY = ['pm_maintenance'];

const NAV_ITEMS = [
  { path: '/pm/dashboard',         label: 'Dashboard',         icon: LayoutDashboard, allow: ['property_manager', 'asset_manager', 'maintenance_supervisor'] },
  { path: '/pm/my-properties',     label: 'My Properties',     icon: Wrench,           allow: TECH_ONLY },
  { path: '/pm/properties',        label: 'Properties',        icon: Building2,        allow: PM_ADMINS },
  { path: '/pm/work-orders',       label: 'Service Requests',  icon: ClipboardList,    allow: ALL },
  { path: '/pm/invoices',          label: 'Invoices',          icon: FileText,         allow: PM_AM },
  { path: '/pm/property-history',  label: 'History',           icon: History,          allow: PM_ADMINS },
  { path: '/pm/contacts',          label: 'Contacts',          icon: PhoneCall,        allow: ALL },
  { path: '/pm/property-requests', label: 'Property Requests', icon: FilePlus2,        allow: PM_AM },
  { path: '/pm/users',             label: 'Users',             icon: Users,            allow: PM_ADMINS },
  { path: '/pm/access-groups',     label: 'Access Groups',     icon: Boxes,            allow: PM_ADMINS },
];

const PortalLayoutPM = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const role = user?.role;
  const items = NAV_ITEMS.filter((it) => it.allow.includes(role));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-[#0A0A0A] pl-safe pr-safe">
      <header className="bg-[#141414] border-b border-[#262626] sticky top-0 z-50 pt-safe-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Logo size="md" showTagline={false} />
              <div className="hidden md:block border-l border-[#262626] pl-4">
                <p className="text-[#A3A3A3] text-xs uppercase tracking-[0.2em]">Property Manager Portal</p>
                <p className="text-[#D4AF37] text-xs font-bold tracking-wider mt-1">LOCAL · TRUSTED · RELIABLE</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <NotificationBell />
              <ThemeToggle />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="pm-profile-menu-trigger"
                    className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-md hover:bg-[#1a1a1a] transition-colors"
                  >
                    <span className="w-8 h-8 rounded-full bg-[#D4AF37] text-[#0A0A0A] flex items-center justify-center text-xs font-bold">
                      {(user?.name || '?').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="hidden sm:inline text-sm text-white max-w-[140px] truncate">{user?.name}</span>
                    <ChevronDown className="hidden sm:inline w-3 h-3 text-[#A3A3A3]" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#141414] border-[#262626] text-white w-56">
                  <DropdownMenuLabel className="text-[#A3A3A3]">
                    <p className="text-white truncate">{user?.name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5">
                      {(user?.role || '').replace(/_/g, ' ')}
                    </p>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem
                    data-testid="pm-profile-menu-item"
                    onClick={() => navigate('/pm/profile')}
                    className="cursor-pointer text-sm text-white hover:!bg-[#D4AF37]/15 hover:!text-[#D4AF37]"
                  >
                    <User className="w-4 h-4 mr-2" /> Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="pm-notifications-menu-item"
                    onClick={() => navigate('/pm/notifications')}
                    className="cursor-pointer text-sm text-white hover:!bg-[#D4AF37]/15 hover:!text-[#D4AF37]"
                  >
                    <BellIcon className="w-4 h-4 mr-2" /> Notifications
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[#262626]" />
                  <DropdownMenuItem
                    data-testid="pm-logout-menu-item"
                    onClick={handleLogout}
                    className="cursor-pointer text-sm text-white hover:!bg-[#7F1D1D]/30 hover:!text-red-300"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Desktop nav */}
      <nav className="bg-[#141414] border-b border-[#262626] sticky top-[60px] sm:top-[72px] z-40 hidden md:block">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {items.map(({ path, label, icon: Icon }) => (
              <Link
                key={path}
                to={path}
                data-testid={`pm-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all duration-200 whitespace-nowrap text-sm ${
                  isActive(path)
                    ? 'border-[#D4AF37] text-[#D4AF37]'
                    : 'border-transparent text-[#A3A3A3] hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile nav button */}
      <div className="md:hidden border-b border-[#262626] bg-[#141414] sticky top-[60px] z-40">
        <button
          data-testid="pm-mobile-nav-toggle"
          onClick={() => setMobileOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 text-white"
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            <Menu className="w-4 h-4" /> Menu
          </span>
          <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">
            {items.find((i) => isActive(i.path))?.label || 'Property Manager Portal'}
          </span>
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/70 z-50" onClick={() => setMobileOpen(false)} data-testid="pm-mobile-nav-backdrop" />
          <aside className="md:hidden fixed left-0 top-0 h-full w-72 bg-[#0D0D0D] border-r border-[#1f1f1f] z-50 p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <Logo size="sm" showTagline={false} />
              <button onClick={() => setMobileOpen(false)} className="text-[#A3A3A3]" data-testid="pm-mobile-nav-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#525252] mb-3">Property Manager Portal</p>
            <ul className="space-y-1">
              {items.map(({ path, label, icon: Icon }) => (
                <li key={path}>
                  <Link
                    to={path}
                    data-testid={`pm-mobile-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm border-l-2 ${
                      isActive(path)
                        ? 'border-[#D4AF37] bg-[#D4AF37]/8 text-[#D4AF37]'
                        : 'border-transparent text-[#A3A3A3] hover:text-white hover:bg-[#1a1a1a]'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-[#1f1f1f] space-y-2">
              <Button
                onClick={() => { setMobileOpen(false); navigate('/pm/profile'); }}
                variant="ghost"
                className="w-full justify-start text-[#A3A3A3] hover:text-white text-sm"
              >
                <SettingsIcon className="w-4 h-4 mr-2" /> Account Settings
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start text-[#A3A3A3] hover:text-red-300 text-sm"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </Button>
            </div>
          </aside>
        </>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 pb-safe">
        <AnnouncementsBanner />
        <ImpersonationBanner />
        <PreviewBanner />
        <ErrorBoundary scope={`pm:${location.pathname}`} key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <footer className="border-t border-[#262626] mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Need Service Now?</p>
            <p className="text-white text-sm">NexFlow dispatch is on standby for your properties — call us anytime.</p>
          </div>
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Contact</p>
            <a href="tel:6142854502" className="text-white text-sm block hover:text-[#D4AF37]"><Phone className="w-3 h-3 inline mr-1" /> 614-285-4502</a>
            <a href="mailto:team@nexflowplumbing.com" className="text-white text-sm block hover:text-[#D4AF37]"><Mail className="w-3 h-3 inline mr-1" /> team@nexflowplumbing.com</a>
            <Link to="/pm/contacts" className="text-[#A3A3A3] text-xs block hover:text-[#D4AF37] mt-1">Full contact directory →</Link>
          </div>
          <div>
            <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em] uppercase mb-2">Local. Trusted. Reliable.</p>
            <p className="text-[#A3A3A3] text-sm">© Nexflow Inc. — Premium Plumbing & Drain Cleaning</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PortalLayoutPM;
